/*
  This example requires Tailwind CSS v2.0+ 
  
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  const colors = require('tailwindcss/colors')
  
  module.exports = {
    // ...
    theme: {
      extend: {
        colors: {
          rose: colors.rose,
        },
      },
    },
  }
  ```
*/
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DocumentTextIcon,
  MapIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  SparklesIcon as SparklesIconOutline, // Renamed to avoid conflict
} from "@heroicons/react/outline";
import { SparklesIcon as SparklesIconSolid } from "@heroicons/react/solid"; // For the Generate button
import InfoMsgAction from "../../UI/InfoMsgAction";
import CardHeadingSearch from "../../UI/CardHeadingSearch";
import useAuth from "../../hooks/useAuth";
import Editor from "./Editor";
import Transcript from "./Transcript";
import { axiosPrivate } from "../../common/axiosPrivate";
import Summary from "./Summary";
import { formatDistanceToNow, differenceInSeconds } from "date-fns";
import Button from "../../UI/Button";
import { useDebounceValue } from "usehooks-ts";
import MindMap from "./MindMap";
import Repurpose from "./Repurpose";
import InfoTooltip from "../../UI/InfoTooltip";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function InboxContent(props) {
    const { auth } = useAuth();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [EditorSaveContent, setEditorSaveContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNow, setSaveNow] = useState(false);

  const tabs = [
    { name: "Create", icon: SparklesIconOutline },
    { name: "Summary", icon: DocumentTextIcon },
    { name: "Transcript", icon: DocumentDuplicateIcon },
    { name: "MindMap", icon: MapIcon },
    { name: "Notes", icon: PencilIcon },
  ];

  const [selectedTab, setSelectedTab] = useState("Summary");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useDebounceValue("", 300);
  const [lastSaved, setLastSaved] = useState(null);

  // State for AI MindMap Query
  const [mindMapQuery, setMindMapQuery] = useState("");
  const [aiMindMapData, setAiMindMapData] = useState(null);
  const [isLoadingAiMindMap, setIsLoadingAiMindMap] = useState(false);
  const [aiMindMapError, setAiMindMapError] = useState(null);
  const [aiDataVersion, setAiDataVersion] = useState(0); // New state for versioning AI data

  const transformAiDataToEpisodeStateFormat = (aiData, originalEpisodeTitle, currentAiDataVersion) => {
    if (!aiData || !aiData.answer || !aiData.key_information) {
      console.warn("transformAiDataToEpisodeStateFormat: Invalid aiData received", aiData);
      return null;
    }

    const baseId = props.selectedEpisodeState?.episode?.id || "unknown-episode";

    return {
      episode: {
        title: aiData.answer.title || originalEpisodeTitle || "AI Generated MindMap",
        summary: {
          episode_summary: {
            summary: aiData.answer.summary || "No summary provided by AI.",
          },
          chapters: aiData.key_information.map((keyInfo, index) => ({
            title: keyInfo.title || `Key Information ${index + 1}`,
            combined_key_takeaway: keyInfo.combined_key_takeaway || "No takeaways provided.",
            combined_main_points: keyInfo.combined_main_points || "No main points provided.",
          })),
        },
        id: `ai-generated-${baseId}-${currentAiDataVersion}`,
        is_summarized: true,
        source: 'ai', // Optional: indicate the source of this data
      },
    };
  };

  const handleEditorSave = async (content) => {
    setIsLoading(true);
    try {
      setLastSaved(new Date());
      setEditorSaveContent(content);
      
      // Make sure saveNow is reset after the content has been saved
      setTimeout(() => {
        setSaveNow(false);
      }, 300);
      
      console.log("InboxContent: Editor save handler received content");
    } catch (error) {
      console.error("Failed to save:", error);
      setSaveError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDemandSave = () => {
    console.log("InboxContent: Manual save requested");
    // Only trigger save if not already saving
    if (!isLoading && !saveNow) {
      setSaveNow(true);
    }
  };

  useEffect(() => {
    setLastSaved(null);
    // Reset AI MindMap data when episode changes
    setAiMindMapData(null);
    setMindMapQuery("");
    setAiMindMapError(null);
    setIsLoadingAiMindMap(false);
    setAiDataVersion(0); // Reset AI data version
  }, [props.selectedEpisodeState]);

  useEffect(() => {
    if (saveError) {
      setTimeout(() => setSaveError(null), 5000);
    }
  }, [saveError]);

  useEffect(() => {
    if (EditorSaveContent) {
        props.selectedEpisodeState.episode.note = EditorSaveContent.data;
      let method = "post";
      let url = `/users/notes`;
      let body = {
        data: EditorSaveContent.data,
        episode_id: EditorSaveContent.episode_id,
      };

      axiosPrivate
        .post(url, body)
        .then((res) => {
          console.log(`Note saved successfully: ${EditorSaveContent.data}!`);
        })
        .catch((err) => {
          console.error(err);
        });
        }
  }, [EditorSaveContent]);

  const onSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
    setDebouncedSearchValue(searchTerm);
  };

  const onSelect = (selectedTabName) => {
    setSelectedTab(selectedTabName);
    setShowSearchBar(selectedTabName === "Transcript");
  };

  const memoizedEpisodeState = useMemo(
    () => props.selectedEpisodeState,
    [props.selectedEpisodeState?.episode?.id, props.selectedEpisodeState?.episode?.is_summarized]
  );

  const handleFetchAiMindMapData = async () => {
    if (!props.selectedEpisodeState?.episode?.id || !mindMapQuery.trim()) {
      setAiMindMapError("An episode must be selected and a query must be entered.");
      return;
    }
    if (!props.selectedEpisodeState?.episode?.summary) {
      setAiMindMapError("Episode summary is not available. Please transcribe and summarize the episode first.");
      return;
    }

    setIsLoadingAiMindMap(true);
    setAiMindMapError(null);

    try {
      const episodeId = props.selectedEpisodeState.episode.id;
      const requestBody = {
        query: mindMapQuery,
        summary: props.selectedEpisodeState.episode.summary,
      };

      const response = await axiosPrivate.post(
        `/mindmap/episodes/${episodeId}/mindmap_query`,
        requestBody
      );
      setAiMindMapData(response.data);
      setAiDataVersion(prevVersion => prevVersion + 1); // Increment version on new data
    } catch (err) {
      console.error("Failed to fetch AI MindMap data:", err);
      setAiMindMapError(
        err.response?.data?.detail || "Failed to generate AI MindMap. Please try again."
      );
      setAiMindMapData(null); // Clear data on error
    } finally {
      setIsLoadingAiMindMap(false);
    }
  };

  const renderContent = () => {
    const isSummaryReady = props.selectedEpisodeState?.episode?.is_summarized;
    const isTranscriptReady = !!props.selectedEpisodeState?.episode?.transcription;

    let mindMapDisplayDataToUse = props.selectedEpisodeState;
    let mindMapComponentKey = props.selectedEpisodeState?.episode?.id || "initial-episode-key";

    if (aiMindMapData) {
      const transformedData = transformAiDataToEpisodeStateFormat(
        aiMindMapData,
        props.selectedEpisodeState?.episode?.title,
        aiDataVersion // Pass the current version
      );
      if (transformedData) {
        mindMapDisplayDataToUse = transformedData;
        mindMapComponentKey = `ai-map-${props.selectedEpisodeState?.episode?.id || 'default'}-${aiDataVersion}`;
      } else {
        mindMapDisplayDataToUse = null;
        mindMapComponentKey = `ai-map-${props.selectedEpisodeState?.episode?.id || 'default'}-${aiDataVersion}-transform-error`;
        if (!aiMindMapError) {
          setAiMindMapError("Error: AI data could not be processed for display.");
        }
      }
    }

    return (
      <>
        <div className={classNames("flex-1", selectedTab === "Summary" ? "" : "hidden")}>
          {isSummaryReady ? (
            <div className="flex-1 flex flex-col items-center justify-between border border-neutral-100 rounded-lg shadow-lg shadow-primary-100 bg-white h-full">
              <Summary
                selectedEpisodeState={props.selectedEpisodeState}
                SelectSegmentHandler={props.SelectSegmentHandler}
              />
            </div>
          ) : (
            <div className="h-full flex flex-col rounded-lg shadow-lg">
              <InfoMsgAction
                msg="This episode has not been processed yet. Process it to unlock AI-powered summaries, transcripts, and insights."
                action="Process Episode"
                onClick={props.HandleTranscribeEpisode}
              />
            </div>
          )}
        </div>

        <div className={classNames("flex-1", selectedTab === "Transcript" ? "" : "hidden")}>
          {isTranscriptReady ? (
            <div className="flex-1 border border-neutral-100 rounded-lg shadow-lg shadow-primary-100 bg-white h-full">
              <Transcript
                data={props.selectedEpisodeState.episode.transcription.paragraphs.paragraphs}
                searchTerm={debouncedSearchValue}
                SelectSegmentHandler={props.SelectSegmentHandler}
              />
            </div>
          ) : (
            <div className="h-full flex flex-col rounded-lg shadow-lg">
              <InfoMsgAction
                msg="This episode has not been processed yet. Process it to unlock AI-powered summaries, transcripts, and insights."
                action="Process Episode"
                onClick={props.HandleTranscribeEpisode}
              />
            </div>
          )}
        </div>

        <div
          className={classNames(
            "flex-1 flex flex-col border border-neutral-100 rounded-lg shadow-lg bg-white",
            selectedTab === "MindMap" ? "h-full" : "hidden"
          )}>
          {isSummaryReady || aiMindMapData ? (
            <>
              <div className="p-4 border-b border-neutral-200 bg-neutral-50">
                <div className="flex items-stretch space-x-3">
                  <input
                    type="text"
                    value={mindMapQuery}
                    onChange={(e) => setMindMapQuery(e.target.value)}
                    placeholder="Ask a question to generate a new MindMap..."
                    className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm py-2 px-3"
                    disabled={isLoadingAiMindMap || !isSummaryReady}
                  />
                  <Button
                    onClick={handleFetchAiMindMapData}
                    disabled={
                      isLoadingAiMindMap ||
                      !mindMapQuery.trim() ||
                      !props.selectedEpisodeState?.episode?.id ||
                      !isSummaryReady
                    }
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                    <SparklesIconSolid className="h-5 w-5 mr-2" />
                    Generate
                  </Button>
                  <span className="inline-flex items-center ml-1">
                    <InfoTooltip text="Each query costs 1 AI credit."></InfoTooltip>
                  </span>
                </div>
                {isLoadingAiMindMap && (
                  <p className="text-xs text-neutral-500 mt-2 animate-pulse">
                    Generating AI MindMap, this may take a moment...
                  </p>
                )}
                {aiMindMapError && <p className="text-xs text-support-red-500 mt-2">{aiMindMapError}</p>}
                {!isSummaryReady && !aiMindMapData && (
                  <p className="text-xs text-support-yellow-700 mt-2">
                    Original episode summary is needed to generate an AI MindMap. Please transcribe and summarize first.
                  </p>
                )}
              </div>

              {mindMapDisplayDataToUse || aiMindMapData ? (
                <div className="flex-1 border-t border-neutral-200 bg-white h-full">
                  <MindMap selectedEpisodeState={mindMapDisplayDataToUse} key={mindMapComponentKey} />
                </div>
              ) : (
                !aiMindMapError && (
                  <div className="h-full flex flex-col items-center justify-center p-4">
                    <InfoMsgAction
                      msg="MindMap data is not available. Generate one using the input above or ensure the episode is summarized."
                      action="Transcribe"
                      onClick={props.HandleTranscribeEpisode}
                      showAction={!isSummaryReady}
                    />
                  </div>
                )
              )}
            </>
          ) : (
            <div className="h-full flex flex-col rounded-lg shadow-lg">
              <InfoMsgAction
                msg="This episode has not been processed yet. Process it to unlock AI-powered summaries, transcripts, and insights."
                action="Process Episode"
                onClick={props.HandleTranscribeEpisode}
              />
            </div>
          )}
        </div>

        <div className={classNames("flex-1 flex flex-col space-y-4", selectedTab === "Notes" ? "" : "hidden")}>
          <div className="flex-1 rounded-lg shadow-lg bg-white">
            <Editor 
              data={memoizedEpisodeState} 
              onChange={handleEditorSave} 
              saveNow={saveNow}
              onDemandSave={handleOnDemandSave}
              id={`notes-editor-container-${memoizedEpisodeState?.episode?.id || 'empty'}`}
              key={`editor-instance-${memoizedEpisodeState?.episode?.id || 'empty'}`}
            />
          </div>

        </div>

        <div className={classNames("flex-1", selectedTab === "Create" ? "" : "hidden")}>
          <div className="border border-neutral-100 rounded-lg shadow-lg shadow-primary-100 bg-white p-4 h-full">
            <Repurpose
              selectedEpisodeState={memoizedEpisodeState}
              HandleTranscribeEpisode={props.HandleTranscribeEpisode}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex-1 flex flex-col space-y-4 mb-12">
      <div className="bg-white">
        <CardHeadingSearch
          title={selectedTab}
          onSearch={onSearch}
          onSelect={onSelect}
          showSearchBar={showSearchBar}
          tabs={tabs}
          selectedTab={selectedTab}
        />
      </div>

      <div className="flex-1 px-4 flex flex-col">{renderContent()}</div>
    </div>
  );
}
